import { useState, useMemo } from 'react';
import {
  useListCurrentSnapshots,
  useListStagedEvents,
  TtOrderMetadata,
  TtOrderSnapshot,
  TtStagedEvent,
} from '@/lib/api/risk/brokerage/tt';
import { TTSummarySection } from './components/TTSummarySection';
import { TTSnapshotOrdersTable } from './components/TTSnapshotOrdersTable';
import {
  buildTtOrderView,
  buildMetadataMap,
  buildFillEventsView,
  TtOrderFilter,
  TtOrderViewResult,
  TtFillEventRow,
} from './transform';
import { useListCustomers } from '@/lib/api/customer';

export function TTDashboardTab() {
  useListCustomers({ minimal: true, hasRiskProfile: true });
  const snapshotsResult = useListCurrentSnapshots();
  // const stagedResult = useListStagedEvents();

  const [activeFilter, setActiveFilter] = useState<TtOrderFilter>('missing_applicable');

  const snapshots = snapshotsResult.data?.data?.snapshots ?? [];
  // const stagedEvents = stagedResult.data?.data?.staged_events ?? [];
  const stagedResult: any = { isLoading: false, isError: false, error: null };
  const stagedEvents: any[] = [];
  const isLoading = snapshotsResult.isLoading || stagedResult.isLoading;

  // Build metadata map from staged events that have associated metadata
  // This will be replaced once the API returns metadata directly
  const metadataByGuid = useMemo(() => {
    // For now, we extract metadata from staged events if they have it
    // The plan assumes the API will be updated to include metadata on snapshots/staged events
    // When that happens, we can use a dedicated metadata endpoint or the returned data
    const metadataList: TtOrderMetadata[] = [
      ...snapshots.map((item: TtOrderSnapshot) => item.meta_data).filter((meta_data: TtOrderMetadata | null): meta_data is TtOrderMetadata  => !!meta_data),
      ...stagedEvents.map((item: TtStagedEvent) => item.meta_data).filter((meta_data: TtOrderMetadata | null): meta_data is TtOrderMetadata => !!meta_data),
    ];

    // Extract from staged events' order_event if available
    // Note: Currently TtStagedEvent has has_associated_metadata but not the actual metadata
    // Once the API is updated to include the metadata object, we'll map it here
    // For now, we return an empty map and rely on text heuristics

    return buildMetadataMap(metadataList);
  }, [stagedEvents, snapshots]);

  // Build the unified order view
  const orderView: TtOrderViewResult = useMemo(
    () =>
      buildTtOrderView({
        snapshots,
        stagedEvents,
        metadataByGuid,
      }),
    [snapshots, stagedEvents, metadataByGuid]
  );

  // Build the fill events list
  const fillEvents: TtFillEventRow[] = useMemo(
    () =>
      buildFillEventsView({
        stagedEvents,
        metadataByGuid,
      }),
    [stagedEvents, metadataByGuid]
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Summary Section */}
      <TTSummarySection
        orderView={orderView}
        activeFilter={activeFilter}
        isLoading={isLoading}
      />

      {/* Unified Orders Table */}
      <div>
        <h3 className="text-md font-medium mb-2">Orders</h3>
        <TTSnapshotOrdersTable
          orderView={orderView}
          fillEvents={fillEvents}
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
          isLoading={isLoading}
        />
      </div>

      {/* Error States */}
      {snapshotsResult.isError && (
        <div className="p-4 border border-red-200 bg-red-50 rounded-md">
          <p className="text-red-600">
            Error loading snapshots: {(snapshotsResult.error as Error)?.message || 'Unknown error'}
          </p>
        </div>
      )}
      {stagedResult.isError && (
        <div className="p-4 border border-red-200 bg-red-50 rounded-md">
          <p className="text-red-600">
            Error loading staged events: {(stagedResult.error as Error)?.message || 'Unknown error'}
          </p>
        </div>
      )}
    </div>
  );
}
